import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import * as AppPropTypes from '../lib/PropTypes';

const SiteContext = React.createContext(null);

export const useSiteContext = () => useContext(SiteContext);

export const useLoading = () => {
    const { loading, setLoading } = useSiteContext();
    return { loading, setLoading };
};

export const useWindowSize = () => {
    const { windowSize } = useSiteContext();
    return windowSize;
};

export const useSite = () => {
    const { site } = useSiteContext();
    return site;
};

export const useSites = () => {
    const { sites } = useSiteContext();
    return sites;
};

export const useMenu = () => {
    const {
        mainMenu,
        mainLeftMenu,
        mainRightMenu,
        footerMenu,
        menuOpen,
        setMenuOpen,
        openMenu,
        closeMenu,
    } = useSiteContext();
    return {
        mainMenu,
        mainLeftMenu,
        mainRightMenu,
        footerMenu,
        menuOpen,
        setMenuOpen,
        openMenu,
        closeMenu,
    };
};

export const useContactPageUrl = () => {
    const { contactPageUrl } = useSiteContext();
    return contactPageUrl;
};

export const useLocales = () => {
    const { locales } = useSiteContext();
    return locales;
};

export const useRootDomain = () => {
    const { rootDomain = null } = useSiteContext();
    return rootDomain;
};

export const useHeader = () => {
    const {
        headerHidden,
        setHeaderHidden,
        scrollDirection,
        setScrollDirection,
        headerDisabled,
        setHeaderDisabled,
    } = useSiteContext();
    return {
        headerHidden,
        setHeaderHidden,
        scrollDirection,
        setScrollDirection,
        headerDisabled,
        setHeaderDisabled,
    };
};

const propTypes = {
    site: AppPropTypes.site,
    rootDomain: PropTypes.string,
    locales: PropTypes.arrayOf(PropTypes.string),
    loading: PropTypes.bool,
    menus: AppPropTypes.menus,
    menuOpen: PropTypes.bool,
    contactPageUrl: PropTypes.string,
    children: PropTypes.node.isRequired,
    windowSize: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
    }),
};

const defaultProps = {
    site: null,
    rootDomain: null,
    locales: null,
    loading: false,
    menus: {},
    menuOpen: false,
    contactPageUrl: null,
    windowSize: {
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    },
};

export const SiteProvider = ({
    site: initialSite,
    sites: initialSites,
    rootDomain,
    locales,
    loading: initialLoading,
    menus,
    menuOpen: initialMenuOpen,
    contactPageUrl,
    windowSize: initialWindowSize,
    children,
}) => {
    const [site] = useState(initialSite);
    const [sites] = useState(initialSites);

    // Locales
    const { locale } = useIntl();
    const [loading, setLoading] = useState(initialLoading);

    // Window size
    const [windowSize, setWindowSize] = useState(initialWindowSize);
    useEffect(() => {
        const onResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [setWindowSize]);

    // Menus
    const {
        main_left: mainLeftMenu = [],
        main_right: mainRightMenu = [],
        footer: footerMenu = [],
    } = menus || {};
    const mainMenu = (mainLeftMenu || []).concat(mainRightMenu || []);

    const [menuOpen, setMenuOpen] = useState(initialMenuOpen);
    const openMenu = useCallback(() => {
        setMenuOpen(true);
    }, [setMenuOpen]);

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
    }, [setMenuOpen]);

    // Header
    const [headerHidden, setHeaderHidden] = useState(false);
    const [headerDisabled, setHeaderDisabled] = useState(false);
    const [scrollDirection, setScrollDirection] = useState(null);

    const value = useMemo(
        () => ({
            site,
            sites,
            contactPageUrl,
            windowSize,
            mainMenu,
            mainLeftMenu,
            mainRightMenu,
            footerMenu,
            menuOpen,
            setMenuOpen,
            openMenu,
            closeMenu,
            locales: locales || [locale],
            loading,
            setLoading,
            headerHidden,
            setHeaderHidden,
            headerDisabled,
            setHeaderDisabled,
            scrollDirection,
            setScrollDirection,
            rootDomain,
        }),
        [
            contactPageUrl,
            windowSize,
            mainMenu,
            mainLeftMenu,
            mainRightMenu,
            footerMenu,
            menuOpen,
            setMenuOpen,
            openMenu,
            closeMenu,
            locale,
            loading,
            setLoading,
            headerHidden,
            setHeaderHidden,
            headerDisabled,
            setHeaderDisabled,
            scrollDirection,
            setScrollDirection,
            rootDomain,
            site,
            sites
        ],
    );

    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

SiteProvider.propTypes = propTypes;
SiteProvider.defaultProps = defaultProps;

export default SiteContext;
